import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { Document, DocumentMode, StaticDataType } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { TranslocoService } from "@jsverse/transloco";
import { FieldArrayType, FormlyModule } from "@ngx-formly/core";
import { isFunction, isNil, isString, pick } from "lodash";
import { MenuItem } from "primeng/api";
import { Card } from "primeng/card";
import { Menu } from "primeng/menu";
import { Tag } from "primeng/tag";
import { Tooltip } from "primeng/tooltip";
import { map, Observable, of, switchMap } from "rxjs";
import { DocumentsService } from "../../features/documents/documents.service";
import { CandidateProfileDialogService } from "../../organization-specific/base-candidate-profile/candidate-profile-dialog.service";
import { NewUiFormElementMapModule } from "../../shared/from-element-map/new-ui-form-element-map.module";
import { StaticDataService } from "../../shared/static-data/static-data.service";
import { TestIdDirective } from "../../shared/test-id/test-id.directive";
import { TranslateDirective } from "../../shared/transloco/translate.directive";

@Component({
  selector: "new-ui-formly-repeat",
  templateUrl: "./new-ui-formly-repeat.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    TestIdDirective,
    NewUiFormElementMapModule,
    Card,
    NewUiButton,
    Tag,
    Tooltip,
    Menu,
    EmptystateComponent,
    TranslateDirective,
  ],
})
export class NewUiFormlyRepeatComponent extends FieldArrayType implements OnInit {
  private readonly language = this.transloco.getActiveLang();

  protected readonly isFunction = isFunction;
  protected readonly isString = isString;

  protected uploadMenu: MenuItem[];

  protected get isAddEnabled(): boolean {
    return (
      !this.props.readonly &&
      this.formControl.enabled &&
      (isNil(this.props.maxCount) || this.props.maxCount > this.field.fieldGroup.length) &&
      !this.props.disabledAddButton
    );
  }

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly documentService: DocumentsService,
    private readonly profileDialogService: CandidateProfileDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setUploadMenu();
  }

  protected isRemoveEnabled(i: number): boolean {
    return (
      !this.props.readonly &&
      this.formControl.enabled &&
      (isNil(this.props.minCount) || i >= this.props.minCount) &&
      !this.props.disabledRemoveButton
    );
  }

  protected openUploadDialog(documentType: string): void {
    this.documentService
      .getByType(documentType, this.formState.organizationId, this.formState.candidateId)
      .pipe(
        switchMap((document) => (document ? of(document) : this.createDocument(documentType))),
        map((document) => ({
          document,
          candidateForm: this.form,
          showDocumentCompletion: this.props.showDocumentCompletion,
        }))
      )
      .subscribe((event) => this.profileDialogService.documentDialog.next(event));
  }

  protected evaluateStringExpression(expression: string, model: unknown, index: number): unknown {
    try {
      const fn = new Function("data", "tabIndex", `return (${expression});`);
      return fn(model, index);
    } catch {
      return undefined;
    }
  }

  private createDocument(type: string): Observable<Document> {
    return this.documentService.createEmptyDocument(
      DocumentMode.Candidate,
      this.formState.organizationId,
      this.formState.candidateId,
      null,
      type
    );
  }

  private setUploadMenu(): void {
    if (!this.props.uploadTypes) return;

    if (this.formState.candidateId) {
      this.staticDataService
        .getStaticData(
          StaticDataType.AllowedUploadFileTypes,
          this.language,
          pick(this.formState, "candidateId", "organizationId")
        )
        .subscribe((data) => {
          this.uploadMenu = this.props.uploadTypes?.map((documentType) => ({
            label: data.find((d) => d.value == documentType)?.label,
            command: (): void => this.openUploadDialog(documentType),
          }));
          this.changeDetector.markForCheck();
        });
    } else {
      this.uploadMenu = null;
    }
  }
}
